import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";

import {
	Button,
	CircularProgress,
	FormControl,
	FormControlLabel,
	FormLabel,
	Icon,
	Radio,
	RadioGroup,
	Typography,
} from "@material-ui/core";
import { useSnackbar } from "notistack";

// Utils
import { createTooltip, formatCurrency } from "app/main/utils/tableUtils";
import { calculateTransferFee } from "./invoiceUtils";
import { getPayout, updatePayout } from "app/services/tripInvoicingServices";
import { incrementDataRevision } from "app/store/tools/revisionSlice";
import { showSnackbar } from "app/main/utils/snackbarUtil";
import { closeFormDialog } from "app/store/tools/formDialogSlice";
import SmartPayJournalEntriesComponent from "../SmartPayJournalEntriesComponent";

const { DisplaySection, renderDataItem } = require("app/main/utils/uiUtils");

const PaymentTypeDescriptions = {
	ACH_PAYMENT:
		"* If the invoice is approved before 2:30 PM ET, the payment will process that business day and you can expect to receive funds in 2-4 business days. If the invoice is approved after 2:30 PM ET, the payment will be processed on the next business day. The transfer fee for this payment is {VAR_TRANSFER_FEE}.",
	WIRE_PAYMENT:
		"* If the invoice is approved before 11:00 AM ET, the payment will be processed that business day, and you can expect to receive funds on the same business day. If the invoice is approved after 11:00 AM ET, the payment will be processed on the next business day. The transfer fee for this payment is {VAR_TRANSFER_FEE}.",
};

const paymentTypesMap = {
	ACH_PAYMENT: "ach",
	WIRE_PAYMENT: "wire",
};

/**
 * This View has 2 modes. Submission Mode and Payout Edit mode
 * Send tripId for Submission or payoutId for editing
 * @param {*} param0
 * @returns
 */
const SmartPayPayoutEditView = ({ dataIds, setTitle, setSize }) => {
	const { carrierId, payoutId } = dataIds;

	const dispatch = useDispatch();
	const snackbar = useSnackbar();

	const [payout, setPayout] = useState();
	const [loading, setLoading] = useState(true);
	const [processing, setProcessing] = useState(false);

	useEffect(() => {
		(async () => {
			const payout = await getPayout(carrierId, payoutId);
			setPayout(payout);
			setPaymentType(payout?.payment_type);
			setLoading(false);
		})();
		setTitle?.("Edit");
		setSize?.("max-w-md");
		// eslint-disable-next-line
	}, []);

	const [paymentType, setPaymentType] = useState("ACH_PAYMENT");

	if (loading) {
		return (
			<div className="flex w-full h-512 flex-col items-center justify-center">
				<CircularProgress />
			</div>
		);
	}

	// Plan's fees per payment type
	const feesPerPaymentType = payout?.smartpay_plan?.payment_types;

	if (!feesPerPaymentType) {
		console.log("No plan found for this carrier");
		return "No SmartPay Plan found for this account";
	}

	// Selected's payment type fees
	const paymentTypeFees = feesPerPaymentType ? feesPerPaymentType[paymentTypesMap[paymentType]] : {};

	const baseTransferFeeCents = paymentTypeFees?.base_transfer_fee_cents;
	const maxTransferFeeCents = paymentTypeFees?.max_transfer_fee_cents;
	const transferFeePercentage = paymentTypeFees?.transfer_fee_percentage;

	const { cappedTransferFeeCents } = calculateTransferFee({
		payoutEntries: payout?.journalEntries,
		incomingAmountCents: 0, // No incoming, just editing payment type
		baseTransferFeeCents,
		transferFeePercentage,
		maxTransferFeeCents,
	});

	// Get original payout gross amount | Same as summing up entries amounts
	const grossAmountCents = payout?.net_amount_cents + payout?.transfer_fee_cents;
	const netAmountCents = grossAmountCents - cappedTransferFeeCents;

	/**
	 * Handle Invoice Submission to Smartpay
	 */
	const handleSubmit = async () => {
		try {
			setProcessing(true);
			await updatePayout(carrierId, payout._id, { smartpayPaymentType: paymentType });
			showSnackbar(snackbar, "Payout updated", "success");
			dispatch(closeFormDialog());
			dispatch(incrementDataRevision({ event: "tripsRevision" }));
		} catch (e) {
			showSnackbar(snackbar, e.errors?.[0]?.message, "error", { duration: 7000 });
		} finally {
			setProcessing(false);
		}
	};

	let transferFeeCalculationText = (
		(baseTransferFeeCents > 0 ? `${formatCurrency(baseTransferFeeCents / 100)}` : "") +
		(transferFeePercentage > 0 ? ` + ${transferFeePercentage}%` : "") +
		(maxTransferFeeCents > 0 ? `  with a cap of ${formatCurrency(maxTransferFeeCents / 100)}` : "")
	).trim();

	if (transferFeeCalculationText.length === 0) {
		transferFeeCalculationText = "0$";
	}

	return (
		<div className={processing ? " opacity-60 pointer-events-none " : ""}>
			<FormControl fullWidth className="mb-8 mx-4 mt-8">
				<div className="flex flex-col md:flex-row md:space-x-16 md:content-center md:items-center border-b-1 border-black mb-16">
					<FormLabel>Payment Type</FormLabel>
					<RadioGroup
						onChange={(event) => {
							setPaymentType(event.target.value);
						}}
						value={paymentType}
						className="flex flex-col md:flex-row"
					>
						<FormControlLabel value={"ACH_PAYMENT"} control={<Radio />} label="ACH (2-4 Business Days*)" />
						<FormControlLabel value={"WIRE_PAYMENT"} control={<Radio />} label="Same Day*" />
					</RadioGroup>
				</div>
				<Typography className="text-10 m-10 mt-0">
					{PaymentTypeDescriptions[paymentType].replace("{VAR_TRANSFER_FEE}", transferFeeCalculationText)}
				</Typography>
			</FormControl>
			<SmartPayJournalEntriesComponent
				title="Included Items"
				payoutEntries={payout?.journalEntries}
				hideStatus={true}
				hidePayout={true}
			/>
			<div className="flex w-full flex-col md:flex-row">
				<DisplaySection classes={{ root: "md:w-full" }} title={`Payout Totals`}>
					<div className="-mt-10">
						{renderDataItem(
							// If calculation is very simple, and value is static, no need to show tooltip
							!transferFeeCalculationText?.includes("%")
								? "Transfer Fee"
								: createTooltip(
										<>
											<span>Transfer Fee</span>
											<Icon className="text-13 ml-4 pt-1">info</Icon>
										</>,
										transferFeeCalculationText
								  ),
							formatCurrency(cappedTransferFeeCents / 100),
							"",
							{}
						)}
						{renderDataItem("Net Amount", formatCurrency(netAmountCents / 100), "", {})}
					</div>
				</DisplaySection>
			</div>
			<div className="flex flex-col w-full">
				<Button
					variant="contained"
					className="bg-green text-white w-full mb-10"
					startIcon={<Icon>disk</Icon>}
					onClick={handleSubmit}
				>
					Save
				</Button>
			</div>
		</div>
	);
};

export default SmartPayPayoutEditView;
